/** AI B2B Leads: the list of enrichment fields, with a checkbox for each. What is ticked is shown in the table and taken into the CSV. */
import { useTranslation } from "react-i18next";
import { Columns3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { ColumnChoice } from "./columns";

interface Props {
  choice: ColumnChoice;
  groups: Array<{ key: string; label: string }>;
}

export function ColumnPicker({ choice, groups }: Props) {
  const { t } = useTranslation();
  const { catalog, keys, isDefault, setKeys, reset } = choice;
  if (catalog.length === 0) return null;

  const toggle = (key: string, on: boolean) => setKeys(on ? [...keys, key] : keys.filter((k) => k !== key));
  const onlyOne = keys.length === 1;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" data-testid="column-picker">
          <Columns3 className="mr-2 h-4 w-4" aria-hidden />
          {t("prospectLeads.columns.button", { shown: keys.length, total: catalog.length })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] max-w-[92vw] p-0" data-testid="column-picker-panel">
        <div className="space-y-1 border-b p-3">
          <p className="text-sm font-medium">{t("prospectLeads.columns.title")}</p>
          <p className="text-xs text-muted-foreground">{t("prospectLeads.columns.help")}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => setKeys(catalog.map((c) => c.key))} data-testid="column-select-all">{t("prospectLeads.columns.selectAll")}</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={reset} disabled={isDefault} data-testid="column-reset">{t("prospectLeads.columns.reset")}</Button>
          </div>
        </div>
        <div className="max-h-[22rem] space-y-3 overflow-y-auto p-3">
          {groups.map((g) => {
            const items = catalog.filter((c) => c.group === g.key);
            if (items.length === 0) return null;
            return (
              <fieldset key={g.key} className="space-y-1.5" data-testid={`column-group-${g.key}`}>
                <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t(`prospectLeads.columns.group.${g.key}`, { defaultValue: g.label })}</legend>
                {items.map((c) => {
                  const on = keys.includes(c.key);
                  const id = `column-${c.key}`;
                  return (
                    <label key={c.key} htmlFor={id} className="flex cursor-pointer items-start gap-2 rounded-sm px-1 py-0.5 hover:bg-muted/50">
                      <Checkbox id={id} checked={on} disabled={on && onlyOne} onCheckedChange={(v) => toggle(c.key, v === true)} className="mt-0.5" data-testid={`column-option-${c.key}`} />
                      <span className="text-sm leading-tight">
                        {t(`prospectLeads.columns.field.${c.key}`, { defaultValue: c.label })}
                        {c.hint && <span className="block text-xs text-muted-foreground">{t(`prospectLeads.columns.hint.${c.key}`, { defaultValue: c.hint })}</span>}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            );
          })}
        </div>
        {onlyOne && <p className="border-t px-3 py-2 text-xs text-muted-foreground" data-testid="column-keep-one">{t("prospectLeads.columns.keepOne")}</p>}
      </PopoverContent>
    </Popover>
  );
}
